import { useRef, useState, useLayoutEffect, useEffect } from 'react'

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

export function useDragData({ dataList, onDragEnd }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [offsets, setOffsets] = useState<number[]>([])

  useLayoutEffect(() => {
    if (
      draggingIndex === null ||
      hoveredIndex === null ||
      draggingIndex === hoveredIndex
    ) {
      setOffsets(Array(dataList.length).fill(0))
      return
    }
    const heights = itemRefs.current.map((el) => el?.offsetHeight ?? 0)
    const draggedHeight = heights[draggingIndex] ?? 0
    const newOffsets = dataList.map((_, i) => {
      // Dragging down
      if (draggingIndex < hoveredIndex) {
        if (i > draggingIndex && i <= hoveredIndex) return -draggedHeight
      }
      // Dragging up
      if (draggingIndex > hoveredIndex) {
        if (i < draggingIndex && i >= hoveredIndex) return draggedHeight
      }
      return 0
    })
    setOffsets(newOffsets)
  }, [draggingIndex, hoveredIndex, dataList])

  useEffect(() => {
    if (!containerRef.current) return

    // Set up drop target for the container (optional, can be removed if not needed)
    const cleanupDropTarget = dropTargetForElements({
      element: containerRef.current,
      getData: () => ({}),
      onDrop: () => {},
    })

    // Set up draggables and drop targets for each item
    const cleanups = itemRefs.current.map((el, i) => {
      const cleanupsForItem: (() => void)[] = []
      if (el) {
        cleanupsForItem.push(
          draggable({
            element: el,
            getInitialData: () => ({
              index: i,
              containerId: containerRef.current?.id,
            }),
            onDragStart: () => setDraggingIndex(i),
            onDrop: () => {
              setDraggingIndex(null)
              setHoveredIndex(null)
            },
          }),
        )
        cleanupsForItem.push(
          dropTargetForElements({
            element: el,
            getData: () => ({ destinationIndex: i }),
            onDragEnter: () => setHoveredIndex(i),
            onDragLeave: () => setHoveredIndex(null),
            onDrop: ({ source, self }) => {
              const sourceIndex = source.data.index

              const destinationIndex = self?.data.destinationIndex
              if (
                typeof sourceIndex === 'number' &&
                typeof destinationIndex === 'number' &&
                sourceIndex !== destinationIndex
              ) {
                onDragEnd({
                  sourceIndex,
                  destinationIndex,
                  id: source.data?.containerId,
                })
              }
            },
          }),
        )
      }
      return () => cleanupsForItem.forEach((fn) => fn())
    })

    return () => {
      cleanupDropTarget()
      cleanups.forEach((cleanup) => cleanup())
    }
  }, [dataList, onDragEnd])

  return {
    containerRef,
    itemRefs,
    draggingIndex,
    offsets,
  }
}
